import clsx from 'clsx';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import invariant from 'tiny-invariant';
import { List } from '../list';
import { CLEAR_ALL_VALUE } from '../list/constants';
import { Menu } from '../menu';
import { Modal, ModalContent, ModalHeader } from '../modal';
import { DropdownButton } from './dropdown-button';
import cssStyles from './styles/base.module.css';
import { getCurrentValueFromChildren } from './utils';
/**
 * Компонент `<Dropdown />` — элемент интерфейса, позволяющий выбрать одно из нескольких заранее определённых значений параметра.
 *
 * @example ``` tsx
 const [value, setValue] = React.useState();

 <Dropdown value={value} onSelect={setValue}>
 <DropdownItem key="1" value="5" text="Пятница" />
 <DropdownItem key="2" value="6" text="Суббота" />
 <DropdownItem key="3" value="7" text="Воскресенье" />
 </Dropdown>
 * ```
 * @param {DropdownProps} props
 * @constructor
 */
export const Dropdown = /*#__PURE__*/forwardRef(({
  className,
  popoverClassName,
  label,
  placeholder = '',
  size = 'm',
  caption,
  disabled = false,
  error = false,
  mobile = false,
  warning = false,
  contentFit,
  closeOnSelect,
  onSelect,
  onPopoverScroll,
  value,
  placement,
  ...props
}, ref) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const selectAndClose = selected => {
    onSelect == null ? void 0 : onSelect(selected);
    close();
  };
  useEffect(() => {
    const isMobileWithOnPopoverScrollProp = !(mobile && onPopoverScroll);
    invariant(isMobileWithOnPopoverScrollProp, 'onPopoverScroll does not work on mobile component.');
  }, [mobile, onPopoverScroll]);
  const showValue = value && value !== CLEAR_ALL_VALUE;
  const rootStyle = useMemo(() => clsx(cssStyles.root, cssStyles[size], {
    [cssStyles.error]: error,
    [cssStyles.open]: open,
    [cssStyles.mobile]: mobile,
    [cssStyles.disabled]: disabled,
    [cssStyles.checked]: !!showValue,
    [cssStyles.withLabel]: !!label
  }, className), [size, error, open, mobile, disabled, showValue, className, label]);
  const currentValue = useMemo(() => getCurrentValueFromChildren(value, props.children), [props.children, value]);
  const buttonProps = {
    open,
    setOpen,
    mobile,
    disabled,
    size,
    rootStyle,
    showValue,
    currentValue,
    placeholder,
    label,
    warning,
    caption,
    error,
    ref
  };
  const button = /*#__PURE__*/React.createElement(DropdownButton, {
    ...buttonProps
  });
  const listSize = size === 'l' ? 'l' : 'm';
  const titleMobileType = mobile === 'curtain' ? 'label' : 'title';
  const listOnSelect = closeOnSelect ? selectAndClose : onSelect;
  return !!mobile ? (/*#__PURE__*/React.createElement(React.Fragment, null, button, /*#__PURE__*/React.createElement(Modal, {
    show: open,
    onClose: close,
    variant: mobile,
    title: placeholder
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    title: placeholder,
    label: placeholder,
    titleMobileType: titleMobileType
  }), /*#__PURE__*/React.createElement(ModalContent, {
    className: popoverClassName
  }, /*#__PURE__*/React.createElement(List, {
    size: listSize,
    selectionMode: "single",
    selectedValues: value,
    onSelect: listOnSelect,
    ...props
  }))))) : (/*#__PURE__*/React.createElement(Menu, {
    className: popoverClassName,
    size: listSize,
    inheritWidth: !contentFit,
    closeFunction: close,
    offset: [0, 4],
    topOffset: label ? 12 : undefined,
    button: button,
    selectionMode: "single",
    selectedValues: value,
    onSelect: onSelect,
    onScroll: onPopoverScroll,
    closeOnSelect: closeOnSelect,
    placement: placement,
    open: open,
    ...props
  }));
});